<!-- lib/components/LanguageSwitcher.svelte -->
<script>
  import { locale, locales } from "svelte-i18n";
  import { onMount } from "svelte";

  let isOpen = false;
  let hasManuallyChanged = false;

  function toggleDropdown() {
    isOpen = !isOpen;
  }

  function selectLanguage(lang) {
    $locale = lang;
    hasManuallyChanged = true;
    isOpen = false;

    // Save to localStorage when manually changed
    if (typeof window !== "undefined") {
      localStorage.setItem("preferred-language", lang);
      window.location.reload(); // Reload to apply changes
    }
  }

  function handleClickOutside(event) {
    if (!event.target.closest(".language-switcher")) {
      isOpen = false;
    }
  }

  // Language options with display names
  const languages = [
    { code: "en", name: "English", flagImg: "/legal/images/Australia.svg" },
    { code: "zh", name: "中文", flagImg: "/legal/images/China.svg" },
  ];

  // Function to get the best matching language from browser locale
  function getBrowserLanguage() {
    if (typeof window === "undefined") return "en";

    const browserLang = navigator.language || navigator.languages?.[0] || "en";
    const langCode = browserLang.toLowerCase().split("-")[0];

    // Check if we support this language
    return languages.some((lang) => lang.code === langCode) ? langCode : "en";
  }

  onMount(() => {
    if (typeof window !== "undefined") {
      const savedLanguage = localStorage.getItem("preferred-language");

      if (
        savedLanguage &&
        languages.some((lang) => lang.code === savedLanguage)
      ) {
        // Use saved preference
        $locale = savedLanguage;
      } else {
        // No saved preference - detect browser locale and save it
        const browserLang = getBrowserLanguage();
        $locale = browserLang;

        // Save this as the initial preference
        localStorage.setItem("preferred-language", browserLang);
      }
    }
  });

  $: currentLanguage =
    languages.find((lang) => lang.code === $locale) || languages[0];
</script>

<svelte:window on:click={handleClickOutside} />

<div class="language-switcher">
  <button
    class="language-button"
    class:open={isOpen}
    on:click={toggleDropdown}
    aria-label="Select language"
    aria-expanded={isOpen}
  >
    <span class="current-language">
      <img
        src={currentLanguage.flagImg}
        alt={`${currentLanguage.name} flag`}
        class="flag"
      />
      <span class="language-name">{currentLanguage.name}</span>
    </span>
    <svg
      class="chevron"
      class:rotated={isOpen}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </button>

  {#if isOpen}
    <div class="dropdown-menu">
      {#each languages as language}
        <button
          class="language-option"
          class:active={language.code === $locale}
          on:click={() => selectLanguage(language.code)}
        >
          <img
            src={language.flagImg}
            alt={`${language.name} flag`}
            class="flag"
          />
          <span class="language-name">{language.name}</span>
        </button>
      {/each}
    </div>
  {/if}
</div>

<style lang="scss">
  .language-switcher {
    position: relative;
    z-index: 1000;
  }

  .language-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    color: var(--text-color2);
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    min-width: 100px;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(210, 165, 82, 0.3);
      color: var(--text-color1);
    }

    &.open {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(210, 165, 82, 0.5);
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(210, 165, 82, 0.3);
    }
  }

  .current-language {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
  }

  .flag {
    width: 18px;
    height: 13px;
    object-fit: cover;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .language-name {
    white-space: nowrap;
  }

  .chevron {
    transition: transform 0.2s ease;
    color: var(--text-color3);

    &.rotated {
      transform: rotate(180deg);
    }
  }

  .dropdown-menu {
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    backdrop-filter: blur(15px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    animation: dropdown-appear 0.2s ease-out;
  }

  .language-option {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    padding: 0.75rem;
    color: var(--text-color2);
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: var(--text-color1);
    }

    &.active {
      background: rgba(210, 165, 82, 0.2);
      color: var(--text-color1);
    }

    &:not(:last-child) {
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
  }

  @keyframes dropdown-appear {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Mobile styles */
  @media (max-width: 768px) {
    .language-button {
      min-width: fit-content;
      padding: 0.4rem 0.6rem;
      font-size: 0.85rem;
    }

    .language-name {
      display: none; /* Hide text on mobile */
    }

    .chevron {
      display: none; /* Hide chevron on mobile */
    }

    .dropdown-menu {
      min-width: 120px;
    }

    .language-option .language-name {
      display: block; /* Show text in dropdown options */
    }
  }

  @media (max-width: 480px) {
    .language-button {
      min-width: fit-content;
      padding: 0.35rem 0.5rem;
      font-size: 0.8rem;
    }

    .language-option {
      padding: 0.6rem;
      font-size: 0.85rem;
    }
  }
</style>
